import { useState, useMemo } from "react";
import { useDraggable } from "@dnd-kit/core";
import { Search, Plus, Settings as SettingsIcon, ShoppingBag, ArrowUpDown, type LucideIcon } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { AppIcon } from "@/components/applications/AppIcon";
import { useApplications } from "@/hooks/useApplications";
import { useAppIconLookup } from "@/hooks/useAppIconLookup";
import { systemAppsFor } from "@/data/appleSystemApps";
import { normalizePlatform } from "@/utils/platformUtils";
import type { FormFactor, IconItem, IconKind, SmplifyExtension } from "./types";
import { AddByBundleIdDialog } from "./AddByBundleIdDialog";
import { useRecentWebClips } from "./useRecentWebClips";
import { WebClipFavicon } from "./WebClipFavicon";
import { cn } from "@/lib/utils";

/** Sort options for the Web Clips list. Persisted to localStorage so admins
 *  who prefer alphabetical browsing don't have to re-pick on every open. */
type WebClipSort = "recent" | "name-asc" | "name-desc";
const WEBCLIP_SORT_KEY = "smplify.homescreen.webclipSort.v1";
function loadWebClipSort(): WebClipSort {
  if (typeof window === "undefined") return "recent";
  const v = window.localStorage.getItem(WEBCLIP_SORT_KEY);
  return v === "name-asc" || v === "name-desc" || v === "recent" ? v : "recent";
}

// Fallback icons for stock Apple apps that aren't on the public App Store
// (so iTunes Lookup returns no artwork). Rendered as a styled tile.
const SYSTEM_APP_FALLBACK_ICONS: Record<string, { Icon: LucideIcon; bg: string; fg: string }> = {
  "com.apple.Preferences": { Icon: SettingsIcon, bg: "bg-muted", fg: "text-foreground/70" },
  "com.apple.AppStore": { Icon: ShoppingBag, bg: "bg-[#0A84FF]", fg: "text-white" },
};

interface AppLibraryPanelProps {
  ff: FormFactor;
  /**
   * Live editor extension. Used to surface Web Clips already on the current
   * layout (across iPhone/iPad variants and folders) so admins can drop them
   * onto a different page or the dock without re-typing the URL.
   */
  ext?: SmplifyExtension | null;
  onAdd: (item: IconItem) => void;
}

export type LibraryEntry = {
  key: string;
  name: string;
  bundleId?: string;
  url?: string;
  iconUrl?: string;
  /** Persisted favicon-vs-generic verdict for Web Clip entries. */
  iconKind?: IconKind;
  source: "tenant" | "system" | "webclip";
  /** ms since epoch — only set for Web Clip suggestions sourced from local
   *  recents. Drives the "Recently added" sort in the Web Clips tab. */
  addedAt?: number;
};

interface DraggableEntryProps {
  entry: LibraryEntry;
  onAdd: () => void;
}

function DraggableEntry({ entry, onAdd }: DraggableEntryProps) {
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: `lib:${entry.key}`,
    data: { kind: "library", entry },
  });
  return (
    <div
      ref={setNodeRef}
      {...listeners}
      {...attributes}
      onDoubleClick={onAdd}
      className={cn(
        "flex flex-col items-center gap-1 p-2 rounded-lg cursor-grab active:cursor-grabbing hover:bg-muted/60 focus:outline-none focus:ring-2 focus:ring-primary",
        isDragging && "opacity-40"
      )}
      role="button"
      tabIndex={0}
      aria-label={`Add ${entry.name}`}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onAdd();
        }
      }}
    >
      {entry.source === "webclip" && entry.url ? (
        // Web Clip suggestion: render the persisted favicon (or generic
        // fallback if IconKind === "generic") so the suggestion tile mirrors
        // exactly how it'll appear once dropped on the canvas. We pass the
        // already-resolved `iconUrl` so this never triggers a fresh probe.
        <WebClipFavicon
          url={entry.url}
          iconSrc={entry.iconUrl ?? null}
          iconKind={entry.iconKind ?? null}
          label={entry.name}
          className="h-12 w-12"
        />
      ) : !entry.iconUrl && entry.bundleId && SYSTEM_APP_FALLBACK_ICONS[entry.bundleId] ? (
        (() => {
          const { Icon, bg, fg } = SYSTEM_APP_FALLBACK_ICONS[entry.bundleId!];
          return (
            <div
              role="img"
              aria-label={entry.name}
              className={cn("h-12 w-12 rounded-xl flex items-center justify-center", bg)}
            >
              <Icon aria-hidden="true" className={cn("h-7 w-7", fg)} />
            </div>
          );
        })()
      ) : (
        <AppIcon iconSrc={entry.iconUrl ?? null} appName={entry.name} className="h-12 w-12 rounded-xl" />
      )}
      <span className="text-[10px] text-center text-foreground/80 line-clamp-2 leading-tight">
        {entry.name}
      </span>
    </div>
  );
}

export function AppLibraryPanel({ ff, ext, onAdd }: AppLibraryPanelProps) {
  const [search, setSearch] = useState("");
  const [tab, setTab] = useState<"apps" | "webclips">("apps");
  const [webclipSort, setWebclipSort] = useState<WebClipSort>(() => loadWebClipSort());
  const [bundleDialogOpen, setBundleDialogOpen] = useState(false);
  const { applications } = useApplications() as { applications: Array<{ name: string; bundleId?: string; icon?: string; platform?: string }> };

  const sysApps = systemAppsFor(ff);

  // Tenant entries — Home Screen Layout payload is iOS/iPadOS only,
  // so exclude macOS apps from the catalog. Apps without a platform
  // (legacy entries) are kept for backwards compatibility.
  const tenantEntries: LibraryEntry[] = (applications || [])
    .filter((a) => !!a.bundleId)
    .filter((a) => {
      const p = normalizePlatform(a.platform);
      return p !== "macos";
    })
    .map((a) => ({
      key: `tenant:${a.bundleId}`,
      name: a.name,
      bundleId: a.bundleId,
      iconUrl: a.icon || undefined,
      source: "tenant",
    }));

  const systemEntries: LibraryEntry[] = sysApps.map((a) => ({
    key: `sys:${a.bundleId}`,
    name: a.name,
    bundleId: a.bundleId,
    source: "system",
  }));

  // Resolve missing icons via iTunes
  const lookupApps = [...tenantEntries, ...systemEntries]
    .filter((e) => !e.iconUrl && e.bundleId)
    .map((e) => ({ bundleId: e.bundleId! }));
  const iconMap = useAppIconLookup(lookupApps);

  const allApps = [...tenantEntries, ...systemEntries].map((e) => ({
    ...e,
    iconUrl: e.iconUrl || (e.bundleId ? iconMap[e.bundleId] : undefined),
  }));

  // "Previously added" Web Clip suggestions — local recents + saved configs
  // + the live editor extension, deduped by URL.
  const webclipSuggestions = useRecentWebClips(ext);
  const webclipEntries: LibraryEntry[] = webclipSuggestions.map((s) => ({
    key: `webclip:${s.key}`,
    name: s.displayName || hostnameOrRaw(s.url),
    url: s.url,
    iconUrl: s.icon,
    iconKind: s.iconKind,
    source: "webclip",
    addedAt: s.addedAt,
  }));

  const term = search.trim().toLowerCase();
  const filteredApps = term
    ? allApps.filter(
        (e) =>
          e.name.toLowerCase().includes(term) ||
          e.bundleId?.toLowerCase().includes(term)
      )
    : allApps;
  // Web Clips: filter by name OR URL, then apply the persisted sort. We sort
  // *after* filtering so the visible order always reflects the active mode
  // even when the search narrows the list down.
  const filteredWebClips = useMemo(() => {
    const base = term
      ? webclipEntries.filter(
          (e) =>
            e.name.toLowerCase().includes(term) ||
            e.url?.toLowerCase().includes(term)
        )
      : webclipEntries;
    const sorted = [...base];
    if (webclipSort === "name-asc") {
      sorted.sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: "base" }));
    } else if (webclipSort === "name-desc") {
      sorted.sort((a, b) => b.name.localeCompare(a.name, undefined, { sensitivity: "base" }));
    } else {
      // "recent": entries with a known addedAt come first (newest → oldest);
      // entries with no timestamp (saved/current-only) sort to the bottom and
      // are ordered alphabetically among themselves for stability.
      sorted.sort((a, b) => {
        const aHas = a.addedAt != null;
        const bHas = b.addedAt != null;
        if (aHas && bHas) return (b.addedAt ?? 0) - (a.addedAt ?? 0);
        if (aHas) return -1;
        if (bHas) return 1;
        return a.name.localeCompare(b.name, undefined, { sensitivity: "base" });
      });
    }
    return sorted;
  }, [term, webclipEntries, webclipSort]);

  function handleSortChange(next: WebClipSort) {
    setWebclipSort(next);
    try {
      window.localStorage.setItem(WEBCLIP_SORT_KEY, next);
    } catch {
      /* best-effort persistence */
    }
  }

  function addApp(entry: LibraryEntry) {
    if (!entry.bundleId) return;
    onAdd({ Type: "Application", BundleID: entry.bundleId, DisplayName: entry.name });
  }

  function addWebClip(entry: LibraryEntry) {
    if (!entry.url) return;
    onAdd({
      Type: "WebClip",
      URL: entry.url,
      DisplayName: entry.name,
      Icon: entry.iconUrl,
      IconKind: entry.iconKind,
    });
  }

  return (
    <div className="flex flex-col h-full border-l bg-card">
      <div className="p-3 border-b space-y-2">
        <div className="flex items-center justify-between">
          <h3 className="text-sm font-semibold">App Library</h3>
        </div>
        <div className="relative">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder={tab === "apps" ? "Search apps…" : "Search Web Clips…"}
            className="pl-8 h-9"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <Tabs value={tab} onValueChange={(v) => setTab(v as "apps" | "webclips")}>
          <TabsList className="grid grid-cols-2 w-full h-8">
            <TabsTrigger value="apps" className="text-xs">Apps</TabsTrigger>
            <TabsTrigger value="webclips" className="text-xs">
              Web Clips{webclipEntries.length > 0 ? ` (${webclipEntries.length})` : ""}
            </TabsTrigger>
          </TabsList>
        </Tabs>
      </div>

      <ScrollArea className="flex-1">
        {tab === "apps" ? (
          <div className="grid grid-cols-3 gap-1 p-3">
            {filteredApps.map((e) => (
              <DraggableEntry key={e.key} entry={e} onAdd={() => addApp(e)} />
            ))}
            {filteredApps.length === 0 && (
              <p className="col-span-3 text-xs text-muted-foreground text-center py-6">
                No apps match "{search}".
              </p>
            )}
          </div>
        ) : (
          <div className="space-y-2 p-3">
            {/* Sort control — only useful in the Web Clips tab. The label is
                visually compact but stays read-aloud-friendly via aria-label. */}
            <div className="flex items-center justify-between gap-2">
              <span className="text-[11px] uppercase tracking-wide text-muted-foreground">
                {filteredWebClips.length} {filteredWebClips.length === 1 ? "clip" : "clips"}
              </span>
              <Select value={webclipSort} onValueChange={(v) => handleSortChange(v as WebClipSort)}>
                <SelectTrigger
                  className="h-7 w-[150px] text-xs"
                  aria-label="Sort Web Clips"
                >
                  <ArrowUpDown className="h-3 w-3 mr-1 text-muted-foreground" aria-hidden="true" />
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="recent" className="text-xs">Recently added</SelectItem>
                  <SelectItem value="name-asc" className="text-xs">Name (A–Z)</SelectItem>
                  <SelectItem value="name-desc" className="text-xs">Name (Z–A)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-3 gap-1">
              {filteredWebClips.map((e) => (
                <DraggableEntry key={e.key} entry={e} onAdd={() => addWebClip(e)} />
              ))}
              {filteredWebClips.length === 0 && (
                <p className="col-span-3 text-xs text-muted-foreground text-center py-6">
                  {webclipEntries.length === 0
                    ? "Previously added Web Clips will appear here. Add one below to get started."
                    : `No Web Clips match "${search}".`}
                </p>
              )}
            </div>
          </div>
        )}
      </ScrollArea>

      <div className="p-3 border-t space-y-2">
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="w-full"
          onClick={() => setBundleDialogOpen(true)}
        >
          <Plus className="h-4 w-4 mr-1" /> Add by Bundle ID / URL
        </Button>
      </div>

      <AddByBundleIdDialog
        open={bundleDialogOpen}
        onOpenChange={setBundleDialogOpen}
        onAdd={onAdd}
      />
    </div>
  );
}

function hostnameOrRaw(input: string): string {
  try {
    return new URL(input.includes("://") ? input : `https://${input}`).hostname;
  } catch {
    return input;
  }
}
